import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LogIn, LogOut, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }) : "—");

export default function Punch() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  const load = useCallback(() => {
    fetch(`${API}/punch/${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d?.detail || "Enlace inválido");
        setInfo(d);
      })
      .catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const punch = async (kind) => {
    setBusy(true);
    setFlash("");
    try {
      const r = await fetch(`${API}/punch/${token}/${kind}`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Error al registrar");
      if (kind === "in") {
        setFlash(d.late_minutes > 0 ? `Entrada registrada (${d.late_minutes} min de tardanza)` : "Entrada registrada — ¡a tiempo!");
      } else {
        setFlash("Salida registrada. ¡Buen descanso!");
      }
      load();
    } catch (e) {
      setFlash(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white border border-rose-200 rounded-2xl p-8 text-center max-w-sm" data-testid="punch-error">
          <p className="text-rose-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-400 text-sm">Cargando…</div>;

  const done = info.check_in && info.check_out;

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="punch-page">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md text-center space-y-6">
        {info.school_logo && (
          <img src={info.school_logo} alt="logo" className="h-14 w-auto mx-auto object-contain" data-testid="punch-school-logo" />
        )}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{info.school_name}</p>
          <h1 className="text-2xl font-bold text-slate-900 mt-1" data-testid="punch-employee-name">{info.employee_name}</h1>
          {info.position && <p className="text-sm text-slate-500">{info.position}</p>}
        </div>
        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
          <Clock className="w-4 h-4" />
          Horario: {info.expected_start || "—"} a {info.expected_end || "—"} · {info.date}
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="punch-status">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Entrada</p>
            <p className="text-lg font-bold text-slate-900 font-mono" data-testid="punch-checkin-time">{fmtTime(info.check_in)}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Salida</p>
            <p className="text-lg font-bold text-slate-900 font-mono" data-testid="punch-checkout-time">{fmtTime(info.check_out)}</p>
          </div>
        </div>
        {flash && (
          <div className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm" data-testid="punch-flash">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> {flash}
          </div>
        )}
        {done && <p className="text-sm text-slate-400">Tu jornada de hoy quedó registrada.</p>}
        <div className="grid gap-3">
          <button
            data-testid="punch-checkin-button"
            disabled={busy || !!info.check_in}
            onClick={() => punch("in")}
            className="w-full bg-blue-800 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 transition-colors"
          >
            <LogIn className="w-5 h-5" /> Registrar entrada{info.check_in ? ` (${fmtTime(info.check_in)})` : ""}
          </button>
          <button
            data-testid="punch-checkout-button"
            disabled={busy || !info.check_in || !!info.check_out}
            onClick={() => punch("out")}
            className="w-full bg-slate-900 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 transition-colors"
          >
            <LogOut className="w-5 h-5" /> Registrar salida{info.check_out ? ` (${fmtTime(info.check_out)})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
